import {
  updatePortalContact,
  useUpdatePortalPreferences,
  type PortalLeaseOut,
} from '@myrentalmate/api-client';
import {
  Alert,
  Button,
  Chip,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';

import { EmailLink, PhoneLink } from '../../components/ContactLinks';
import { FormDialog } from '../../components/FormDialog';
import { optional, PHONE_FIELD } from '../../components/FormFields';
import { StatRow, StatTile } from '../../components/Stats';
import { formatPhone } from '../../lib/contact';
import { formatDate, formatMoney } from '../../lib/format';
import { NotLinked } from './NotLinked';
import { PayDialog } from './PayDialog';
import { usePortalHome } from './usePortalHome';

function LeaseCard({
  lease,
  canPay,
  onPay,
}: {
  lease: PortalLeaseOut;
  canPay: boolean;
  onPay: () => void;
}) {
  const processing = Number(lease.processing);
  const payable = Number(lease.balance) - processing;
  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Stack spacing={2}>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            {lease.label}
          </Typography>
          {lease.status === 'ended' && <Chip size="small" label="Ended" />}
        </Stack>
        <StatRow>
          <StatTile
            label={Number(lease.balance) < 0 ? 'Credit' : 'You owe'}
            value={formatMoney(Math.abs(Number(lease.balance)).toFixed(2))}
          />
          <StatTile
            label="Monthly Rent"
            value={formatMoney(lease.rent_amount)}
            hint={`Due on day ${lease.due_day}`}
          />
          <StatTile
            label="Lease"
            value={formatDate(lease.start_date)}
            hint={lease.end_date ? `to ${formatDate(lease.end_date)}` : 'Month to month'}
          />
        </StatRow>
        {processing > 0 && (
          <Alert severity="info">
            A bank payment of {formatMoney(lease.processing)} is on its way. It takes several
            business days to clear.
          </Alert>
        )}
        {canPay && payable >= 0.5 && (
          <Button variant="contained" sx={{ alignSelf: 'flex-start' }} onClick={onPay}>
            Pay now
          </Button>
        )}
      </Stack>
    </Paper>
  );
}

export function PortalHomePage() {
  const { home, notLinked, isError } = usePortalHome();
  const [paying, setPaying] = useState<PortalLeaseOut | null>(null);
  const [editing, setEditing] = useState(false);
  const queryClient = useQueryClient();
  const preferences = useUpdatePortalPreferences({
    mutation: { onSuccess: () => queryClient.invalidateQueries() },
  });

  if (notLinked) return <NotLinked />;
  if (isError)
    return <Alert severity="error">This page couldn&apos;t load. Please try again.</Alert>;
  if (!home) return null;
  const { business } = home;
  return (
    <Stack spacing={3}>
      <Typography variant="h4">Welcome, {home.first_name}</Typography>
      {home.leases.length === 0 && (
        <Alert severity="info">Your lease will show here once your property manager adds it.</Alert>
      )}
      {home.leases.map((lease) => (
        <LeaseCard
          key={lease.id}
          lease={lease}
          canPay={home.pay.online}
          onPay={() => setPaying(lease)}
        />
      ))}
      {business.payment_instructions && (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="h6">
            {home.pay.online ? 'Other ways to pay' : 'How to pay'}
          </Typography>
          <Typography sx={{ whiteSpace: 'pre-line' }}>{business.payment_instructions}</Typography>
        </Paper>
      )}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
        <Paper variant="outlined" sx={{ p: 3, flex: 1 }}>
          <Typography variant="h6">Your Contact Details</Typography>
          <Typography>
            {home.first_name} {home.last_name}
          </Typography>
          <Typography>{home.email ?? 'No email on file'}</Typography>
          <Typography>{home.phone ? formatPhone(home.phone) : 'No phone on file'}</Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 1, ml: -0.5 }}>
            <Button size="small" onClick={() => setEditing(true)}>
              Change phone
            </Button>
            <Button size="small" component={Link} to="/account">
              Change email
            </Button>
          </Stack>
          <FormControlLabel
            sx={{ display: 'flex', mt: 1 }}
            label="Email Me Rent Reminders"
            control={
              <Switch
                checked={home.email_reminders}
                disabled={preferences.isPending}
                onChange={(e) =>
                  preferences.mutate({ data: { email_reminders: e.target.checked } })
                }
              />
            }
          />
          <Typography variant="body2" color="text.secondary">
            Receipts and notices about your payments still come by email.
          </Typography>
        </Paper>
        <Paper variant="outlined" sx={{ p: 3, flex: 1 }}>
          <Typography variant="h6">{business.name}</Typography>
          {business.phone && (
            <Typography>
              <PhoneLink phone={business.phone} />
            </Typography>
          )}
          {business.email && (
            <Typography>
              <EmailLink email={business.email} />
            </Typography>
          )}
          {!business.phone && !business.email && (
            <Typography color="text.secondary">No contact details yet.</Typography>
          )}
        </Paper>
      </Stack>
      {paying && <PayDialog lease={paying} pay={home.pay} onClose={() => setPaying(null)} />}
      {editing && (
        <FormDialog
          title="Change Phone"
          fields={[PHONE_FIELD]}
          initial={{ phone: formatPhone(home.phone) }}
          onSubmit={(values) => updatePortalContact({ phone: optional(values, 'phone') })}
          onClose={() => setEditing(false)}
        />
      )}
    </Stack>
  );
}
